import { Component, inject } from '@angular/core';
import { MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatIcon } from '@angular/material/icon';
import { MatButton, MatIconButton } from '@angular/material/button';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Neighbourhood } from '../../model/story/neighbourhood.model';
import { StoryTheme } from '../../model/story/story-theme.model';
import { StoryTag } from '../../model/story/story-tag.model';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatOption, MatSelect } from '@angular/material/select';
import { NeighbourhoodService } from '../../services/neighbourhood.service';
import { ThemeService } from '../../services/theme.service';
import { TagService } from '../../services/tag.service';
import { debounceTime } from 'rxjs';
import { AppStateStore } from '../../store/app-state.store';
import { MatInput } from '@angular/material/input';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';

@Component({
  selector: 'app-filters-bottom-sheet',
  imports: [
    MatIcon,
    MatIconButton,
    MatFormField,
    MatLabel,
    MatOption,
    MatSelect,
    ReactiveFormsModule,
    MatButton,
    MatInput,
    MatButtonToggle,
    MatButtonToggleGroup,
  ],
  templateUrl: './filters-bottom-sheet.html',
  styleUrl: './filters-bottom-sheet.scss',
})
export class FiltersBottomSheet {
  public sheet =
    inject<MatBottomSheetRef<FiltersBottomSheet>>(MatBottomSheetRef);
  public readonly appStateStore = inject(AppStateStore);
  private readonly neighbourhoodService = inject(NeighbourhoodService);
  private readonly themeService = inject(ThemeService);
  private readonly tagService = inject(TagService);
  private readonly STORAGE_KEY = 'museum-vlaardingen-filters-state';
  /**
   * Filters
   */
  // Search query
  public searchQueryInput: FormControl = new FormControl();
  private searchQuery = '';
  // Neighbourhood filter
  public neighbourhoodSelect = new FormControl<Neighbourhood[] | null>([]);
  public neighbourhoodList: Neighbourhood[] = [];
  private selectedAreaIdList: string[] = [];
  // Theme filter
  public storyThemeSelect = new FormControl<StoryTheme[] | null>([]);
  public storyThemeList: StoryTheme[] = [];
  private selectedThemeIdList: string[] = [];
  // Tag filter
  public tagSelect = new FormControl<StoryTag[] | null>([]);
  public storyTagList: StoryTag[] = [];
  private selectedTagIdList: string[] = [];
  // Story type
  public storyTypeInput: FormControl = new FormControl('all');
  private storyType = 'all';
  // Active filter
  public activeInput: FormControl = new FormControl('all');
  private active = 'all';
  // Reset state
  private resetFiltersInProgress = false;

  constructor() {
    // Filters
    this.loadFiltersData();
    this.observeFilterChanges();
    this.restoreFiltersState();
  }

  /**
   * Filters
   */
  private loadFiltersData(): void {
    // Neighbourhood
    this.neighbourhoodService.getNeighbourhoodList()
      .subscribe((neighbourhoodList: Neighbourhood[]): Neighbourhood[] => this.neighbourhoodList = neighbourhoodList);
    // Theme
    this.themeService.getThemeList()
      .subscribe((storyThemeList: StoryTheme[]): StoryTheme[] => this.storyThemeList = storyThemeList);
    // Tag
    this.tagService.getTagList()
      .subscribe((storyTagList: StoryTag[]): StoryTag[] => this.storyTagList = storyTagList);
  }

  private observeFilterChanges(): void {
    // Search query
    this.searchQueryInput.valueChanges.pipe(debounceTime(200)).subscribe((value: string) => {
      this.searchQuery = value;
      this.loadStoryData();
    });
    // Neighbourhood
    this.neighbourhoodSelect.valueChanges.subscribe((selectedNeighbourhoodList: Neighbourhood[] | null): void => {
      if (selectedNeighbourhoodList !== null) {
        this.selectedAreaIdList = selectedNeighbourhoodList.map(neighbourhood => neighbourhood.id);
        this.loadStoryData();
      }
    });
    // Theme
    this.storyThemeSelect.valueChanges.subscribe((selectedThemeIdList: StoryTheme[] | null): void => {
      if (selectedThemeIdList !== null) {
        this.selectedThemeIdList = selectedThemeIdList.map((storyTheme: StoryTheme) => storyTheme.id);
        this.loadStoryData();
      }
    });
    // Tag
    this.tagSelect.valueChanges.subscribe((selectedTagList: StoryTag[] | null): void => {
      if (selectedTagList !== null) {
        this.selectedTagIdList = selectedTagList.map((tag: StoryTag) => tag.id);
        this.loadStoryData();
      }
    });
    // Story Type
    this.storyTypeInput.valueChanges.subscribe((value: string) => {
      this.storyType = value;
      this.loadStoryData();
    });
    // Active
    this.activeInput.valueChanges.subscribe((value: string) => {
      this.active = value;
      this.loadStoryData();
    });
  }

  /**
   * Clear all filters
   */
  public clearFilters(): void {
    this.resetFiltersInProgress = true;
    this.searchQueryInput.reset('', {emitEvent: false});
    this.neighbourhoodSelect.reset([], {emitEvent: false});
    this.storyThemeSelect.reset([], {emitEvent: false});
    this.tagSelect.reset([], {emitEvent: false});
    this.storyTypeInput.reset('all', {emitEvent: false});
    this.activeInput.reset('all', {emitEvent: false});
    this.resetFiltersInProgress = false;

    // also reset your "selected…" variables if needed
    this.searchQuery = '';
    this.selectedAreaIdList = [];
    this.selectedThemeIdList = [];
    this.selectedTagIdList = [];
    this.storyType = 'all';
    this.active = 'all';

    // Reload data
    this.loadStoryData();
  }

  public compareById(o1: {id: string}, o2: {id: string}): boolean {
    return o1?.id === o2?.id;
  }

  private saveFiltersState(): void {
    // Build the filter state to store
    const state = {
      searchQuery: this.searchQuery,
      selectedAreaIdList: this.selectedAreaIdList,
      selectedThemeIdList: this.selectedThemeIdList,
      selectedTagIdList: this.selectedTagIdList,
      neighbourhoods: this.neighbourhoodSelect.value,
      themes: this.storyThemeSelect.value,
      tags: this.tagSelect.value
    };
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(state));
  }

  private restoreFiltersState(): void {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (!stored) {
      return;
    }

    try {
      const state = JSON.parse(stored);

      // Restore internal state
      if (state.searchQuery) {
        this.searchQuery = state.searchQuery;
        this.searchQueryInput.setValue(state.searchQuery, { emitEvent: false });
      }

      if (state.selectedAreaIdList) {
        this.selectedAreaIdList = state.selectedAreaIdList;
      }
      if (state.neighbourhoods) {
        this.neighbourhoodSelect.setValue(state.neighbourhoods, { emitEvent: false });
      }

      if (state.selectedThemeIdList) {
        this.selectedThemeIdList = state.selectedThemeIdList;
      }
      if (state.themes) {
        this.storyThemeSelect.setValue(state.themes, { emitEvent: false });
      }

      if (state.selectedTagIdList) {
        this.selectedTagIdList = state.selectedTagIdList;
      }
      if (state.tags) {
        this.tagSelect.setValue(state.tags, { emitEvent: false });
      }

      // Initial load with restored filters
      this.loadStoryData();

    } catch (e) {
      console.error('Failed to restore filters state', e);
      localStorage.removeItem(this.STORAGE_KEY);
    }
  }

  /**
   * Load filtered data from the back-end
   */
  public loadStoryData(): void {
    // Avoid loading when restoring the filters
    if (this.resetFiltersInProgress) {
      return;
    }
    // General filters and pagination
    const params: any = {
      search: this.searchQuery,
      'areas[]': this.selectedAreaIdList,
      'themas[]': this.selectedThemeIdList,
      'tags[]': this.selectedTagIdList,
    };

    if (this.storyType !== 'all') {
      params['project'] = this.storyType === 'projects';
    }

    if (this.active !== 'all') {
      params['active'] = this.active === 'active'
    }

    // Load data
    this.appStateStore.loadStoryListData({ params });
    this.saveFiltersState();
  }
}
